import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  Clock,
  Shield,
  Heart,
  Award
} from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Find Hospitals', href: '/hospitals' },
    { name: 'Find Pharmacies', href: '/pharmacies' },
    { name: 'Doctor Directory', href: '/doctors' },
    { name: 'Emergency Services', href: '/emergency' },
    { name: 'About Us', href: '/about' },
    { name: 'Our Services', href: '/services' }
  ];

  const supportLinks = [
    { name: 'Help Center', href: '/shared/help' },
    { name: 'Contact Support', href: '/contact' },
    { name: 'Privacy Policy', href: '/shared/privacy-policy' },
    { name: 'Support', href: '/shared/support' }
  ];

  const features = [
    { icon: Clock, text: '24/7 Emergency Services' },
    { icon: Shield, text: 'Secure & Private' },
    { icon: Heart, text: 'Trusted Healthcare' },
    { icon: Award, text: 'Verified Providers' }
  ];

  return (
    <footer className="bg-secondary-800 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="relative">
                <img 
                  className="h-12 w-12 rounded-full object-cover border-2 border-primary-300 shadow-md" 
                  src="/images/logo.png" 
                  alt="MediGo" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden h-12 w-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-500 items-center justify-center border-2 border-primary-300 shadow-md">
                  <span className="text-white font-bold text-xl">M</span>
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                MediGo
              </div>
            </Link>
            
            <p className="text-gray-300 text-sm leading-relaxed">
              Your trusted online healthcare platform connecting patients with quality healthcare 
              providers across Kenya for better health outcomes.
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <feature.icon className="w-4 h-4 text-primary-400" />
                  <span className="text-xs text-gray-300">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex space-x-4 pt-4">
              <a 
                href="https://facebook.com/MediGo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary-700 hover:bg-primary-600 transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://twitter.com/MediGo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary-700 hover:bg-primary-600 transition-colors duration-200"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a 
                href="https://instagram.com/MediGo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary-700 hover:bg-primary-600 transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://linkedin.com/company/MediGo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary-700 hover:bg-primary-600 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Support</h3>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">
                    100% Online Platform
                  </p>
                  <p className="text-gray-400 text-xs">
                    Serving All of Kenya
                  </p>
                </div>
              </div>
              
              <a href="tel:+254797310641" className="flex items-center space-x-3 hover:text-primary-400 transition-colors">
                <Phone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">+254 797 310 641</p>
                  <p className="text-gray-400 text-xs">24/7 Support</p>
                </div>
              </a>
              
              <a href="mailto:support@medigo.co.ke" className="flex items-center space-x-3 hover:text-primary-400 transition-colors">
                <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">support@medigo.co.ke</p>
                  <p className="text-gray-400 text-xs">We reply within 24hrs</p>
                </div>
              </a>
            </div>

            {/* Emergency Notice */}
            <div className="bg-red-600 bg-opacity-20 border border-red-500 border-opacity-30 rounded-lg p-3 mt-4">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-red-400" />
                <a href="tel:911" className="text-red-300 text-sm font-medium hover:text-red-200 transition-colors">
                  Emergency: 911
                </a>
              </div>
              <p className="text-red-200 text-xs mt-1">
                For life-threatening emergencies, call 911 immediately
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-secondary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} MediGo. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Licensed Online Healthcare Technology Platform
              </p>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-400">
              <Link to="/shared/privacy-policy" className="hover:text-primary-400 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/shared/support" className="hover:text-primary-400 transition-colors duration-200">
                Support
              </Link>
              <Link to="/shared/help" className="hover:text-primary-400 transition-colors duration-200">
                Help Center
              </Link>
            </div>
          </div>
          
          {/* Additional Info */}
          <div className="mt-4 pt-4 border-t border-secondary-700">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
              <p className="text-gray-500 text-xs text-center sm:text-left">
                MediGo is an online healthcare technology platform connecting patients with verified healthcare providers across Kenya.
              </p>
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span>Made with ❤️ for Kenya</span>
                <span>•</span>
                <span>medigo.co.ke</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;